import { merge } from 'lodash';
import { useMemo, useEffect } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider, ThemeOptions } from '@mui/material';

import { heIL, enUS } from '@mui/material/locale';
import { customShadows } from './custom-shadows';
import { createContrast } from './options/contrast';
import { componentsOverrides } from './overrides';
import palette from './palette';
import { shadows } from './shadows';
import { fontFamily, fontFamilyRubik, typography } from './typography';
import RTL from './options/right-to-left';

type Props = {
  children: React.ReactNode;
  language?: 'en' | 'he';
};

export function ThemeProvider({ children, language = 'he' }: Props) {
  const isDarkMode = false;
  const mode = 'light';

  // Use provided language or detect from domain
  const currentLanguage = language;
  const direction = currentLanguage === 'he' ? 'rtl' : 'ltr';
  const muiLocale = currentLanguage === 'he' ? heIL : enUS;

  const contrast = createContrast('default', 'light');

  const memoizedValue = useMemo(() => {
    const themeValue = {
      palette: isDarkMode ? { ...palette.dark, mode } : { ...palette.light, mode },
      customShadows: customShadows.light,
      direction,
      shadows: shadows(mode),
      shape: { borderRadius: 8 },
      typography: {
        ...typography,
        fontFamily: direction === 'rtl' ? fontFamilyRubik : fontFamily,
      },

      // Overrides
      MuiOutlinedInput: {
        notched: false,
      },
      MuiInputLabel: {
        shrink: true,
      },
    };

    return themeValue;
  }, [isDarkMode, direction]);

  const theme = createTheme(memoizedValue as ThemeOptions, muiLocale);

  theme.components = merge(componentsOverrides(theme), contrast.components, {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Rubik:wght@400;500;600;700;800&display=swap');

        body {
          text-align: inherit;
          background-color: ${palette.light.grey[50]};
        }

        *::-webkit-scrollbar {
          width: 0.4em;
          height: 0.6em;
        }

        *::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }

        *::-webkit-scrollbar-thumb {
          background-color: #d0d5dd;
          border-radius: 8px;
        }

        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        input:-webkit-autofill,
        .MuiInputBase-root:has(> input:-webkit-autofill),
        .MuiInputBase-root:has(> input:-webkit-autofill) .MuiInputAdornment-root {
          background-color: #e8f0fe;
        }
      `,
    },
  });

  // NOTE: Had to remove this code for now because it was causing MUI modals to shift a bit when oppened
  // .hide-scrollbar::-webkit-scrollbar {
  //   display: none;
  // }
  // *::-webkit-scrollbar {
  //    width: 0.4em;
  //    height: 0.6em;
  //  }

  //  *::-webkit-scrollbar-track {
  //    background: #f1f1f1;
  //    borde-radius: 8px;
  //  }

  //  *::-webkit-scrollbar-thumb {
  //    background-color: #d0d5dd;
  //    border-radius: 8px;
  //  }

  return (
    <MuiThemeProvider theme={theme}>
      <RTL themeDirection={direction}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}
